import { ReactNode } from "react";
import { computeEstimate, type EstimateConfig } from "../lib/rates";
import { money, useRateCard } from "../lib/store";
import { Icon } from "./Icons";
import { Meter } from "./ui";

/** Interactive scope → fee configurator. Controlled: pass config + onChange.
 *  Reads the studio's editable rate card. `canDiscount` shows the discount editor.
 *  `readOnly` (or omitting onChange) shows the fee but blocks every edit — used
 *  when the viewer's role doesn't include the estimator. */
export function ScopeConfigurator({ config, onChange, footer, canDiscount, readOnly, lockedNote }: {
  config: EstimateConfig; onChange?: (c: EstimateConfig) => void; footer?: ReactNode; canDiscount?: boolean; readOnly?: boolean;
  /** Why it is read-only, when it is not simply a matter of permissions. */
  lockedNote?: string;
}) {
  const rc = useRateCard();
  const est = computeEstimate(config, rc);
  const ccy = config.currency;
  const locked = readOnly || !onChange;
  const noBudget = !(config.budget > 0);
  const emit = (c: EstimateConfig) => { if (!locked) onChange!(c); };

  const toggleDiscipline = (k: string) =>
    emit({ ...config, disciplines: config.disciplines.includes(k) ? config.disciplines.filter((x) => x !== k) : [...config.disciplines, k] });
  const toggleAddon = (k: string) =>
    emit({ ...config, addons: config.addons.includes(k) ? config.addons.filter((x) => x !== k) : [...config.addons, k] });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 22, alignItems: "start" }} className="scope-grid">
      {/* -------- configure -------- */}
      <div className="col" style={{ gap: 20 }}>
        {locked && (
          <div className="panel" style={{ padding: "10px 13px", fontSize: 12.5, borderStyle: "dashed" }}>
            {lockedNote ?? "View only — your role doesn't include the fee estimator, so the scope can't be changed here."}
          </div>
        )}
        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 14 }}>Your project</div>
          <div className="field" style={{ minWidth: 200 }}>
            <label>Construction budget</label>
            <div className="row" style={{ gap: 8 }}>
              <select className="select" style={{ width: 90 }} value={ccy} disabled={locked} onChange={(e) => emit({ ...config, currency: e.target.value })}>
                {["USD", "EUR", "GBP", "GYD"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input className="input" type="number" value={config.budget || ""} disabled={locked} onChange={(e) => emit({ ...config, budget: Number(e.target.value) })} placeholder="250000" />
            </div>
          </div>
          <input type="range" min={50000} max={2000000} step={10000} value={Math.min(2000000, config.budget || 0)} disabled={locked} onChange={(e) => emit({ ...config, budget: Number(e.target.value) })}
            style={{ width: "100%", marginTop: 14, accentColor: "var(--accent)" }} />
          <div className="row between dim" style={{ fontSize: 11, marginTop: 2 }}><span>{money(50000, ccy)}</span><span>{money(2000000, ccy)}+</span></div>

          <div style={{ marginTop: 18 }}>
            <div className="kicker" style={{ marginBottom: 10 }}>Level of detail</div>
            <div className="row" style={{ gap: 8 }}>
              {rc.complexity.map((x) => (
                <button key={x.key} onClick={() => emit({ ...config, complexity: x.key })} disabled={locked} className="panel" title={x.blurb}
                  style={{ flex: 1, padding: "10px 12px", cursor: "pointer", textAlign: "left", borderColor: config.complexity === x.key ? "var(--accent)" : "var(--line)", background: config.complexity === x.key ? "var(--accent-soft)" : "var(--card)" }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{x.label}</div>
                  <div className="dim" style={{ fontSize: 11 }}>×{x.mult.toFixed(2)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 14 }}>Design scope · disciplines</div>
          <div className="col" style={{ gap: 8 }}>
            {rc.disciplines.map((d) => {
              const on = config.disciplines.includes(d.key);
              const line = est.lines.find((l) => l.key === d.key);
              return <ScopeRow key={d.key} on={on} disabled={locked} onToggle={() => toggleDiscipline(d.key)} label={d.label} blurb={d.blurb}
                amount={on && line ? money(line.amount, ccy) : `~${(d.pct * 100).toFixed(1)}%`} />;
            })}
          </div>
        </div>

        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 14 }}>Optional services</div>
          <div className="col" style={{ gap: 8 }}>
            {rc.addons.map((a) => {
              const disabled = !!a.requires && !config.disciplines.includes(a.requires);
              const on = config.addons.includes(a.key);
              const line = est.lines.find((l) => l.key === a.key);
              return <ScopeRow key={a.key} on={on && !disabled} disabled={disabled || locked} onToggle={() => !disabled && toggleAddon(a.key)}
                label={a.label} blurb={disabled ? `Requires ${a.requires}` : a.blurb}
                amount={on && line ? money(line.amount, ccy) : a.kind === "fixed" ? money(a.value, ccy) : `~${(a.value * 100).toFixed(1)}%`} />;
            })}
          </div>
        </div>

        {canDiscount && (
          <div className="card pad">
            <div className="row between">
              <div><div className="kicker">Client discount</div><div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>Shown to the client on their dashboard.</div></div>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <input className="input" type="number" min={0} max={100} value={config.discountPct || 0} disabled={locked} onChange={(e) => emit({ ...config, discountPct: Math.max(0, Math.min(100, Number(e.target.value))) })} style={{ width: 80, textAlign: "right" }} />
                <span className="mono">%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* -------- live summary -------- */}
      <div style={{ position: "sticky", top: 90 }}>
        <div className="card pad" style={{ background: "var(--ink)", color: "var(--paper)", border: "none" }}>
          {/* Without a budget every discipline falls back to its floor, so the
              total is a set of minimums — not a fee for THIS project. Saying
              "0.0% of construction budget" dresses that up as a real number. */}
          <div className="kicker" style={{ color: "rgba(255,255,255,.5)" }}>{noBudget ? "Minimum fees" : "Estimated design fee"}</div>
          <div className="serif" style={{ fontSize: 44, marginTop: 8, lineHeight: 1 }}>{money(est.total, ccy)}</div>
          <div style={{ opacity: 0.6, fontSize: 13, marginTop: 6 }}>
            {noBudget
              ? "Starting fees only — set a construction budget above to price this project"
              : `${(est.pctOfBudget * 100).toFixed(1)}% of construction budget`}
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,.14)", margin: "18px 0" }} />
          <div className="col" style={{ gap: 9 }}>
            {est.lines.length === 0 && <div style={{ opacity: 0.5, fontSize: 13 }}>Select a discipline to begin.</div>}
            {est.lines.map((l) => (
              <div key={l.key} className="row between" style={{ fontSize: 13.5 }}><span style={{ opacity: 0.85 }}>{l.label}</span><span className="mono">{money(l.amount, ccy)}</span></div>
            ))}
          </div>
          {est.discount > 0 && (
            <>
              <div className="row between" style={{ fontSize: 13.5, marginTop: 9, opacity: 0.85 }}><span>Subtotal</span><span className="mono">{money(est.gross, ccy)}</span></div>
              <div className="row between" style={{ fontSize: 13.5, marginTop: 6, color: "var(--accent)" }}><span>Discount ({est.discountPct}%)</span><span className="mono">−{money(est.discount, ccy)}</span></div>
            </>
          )}
          <div style={{ height: 1, background: "rgba(255,255,255,.14)", margin: "16px 0" }} />
          <div className="row between" style={{ fontWeight: 600 }}><span>Total fee</span><span className="serif" style={{ fontSize: 20 }}>{money(est.total, ccy)}</span></div>
        </div>

        {/* disbursement schedule */}
        <div className="card pad" style={{ marginTop: 14 }}>
          <div className="kicker" style={{ marginBottom: 4 }}>Disbursement schedule</div>
          <div className="dim" style={{ fontSize: 12, marginBottom: 14 }}>How the fee is paid across work phases</div>
          <div className="col" style={{ gap: 13 }}>
            {est.phases.map((s) => (
              <div key={s.key}>
                <div className="row between" style={{ fontSize: 13, marginBottom: 5 }}>
                  <span className="row" style={{ gap: 7 }}>{s.label}{s.construction && <span className="badge accent" style={{ fontSize: 9, padding: "1px 6px" }}>during construction</span>}</span>
                  <span className="mono">{money(s.amount, ccy)} · {s.pctLabel}%</span>
                </div>
                <Meter value={s.pct * 100} thin color={s.construction ? "var(--sage)" : undefined} />
              </div>
            ))}
          </div>
          <div className="divider" style={{ margin: "14px 0" }} />
          <div className="row between" style={{ fontSize: 13 }}><span className="dim">Paid during construction</span><span className="mono" style={{ fontWeight: 600 }}>{money(est.constructionAmount, ccy)}</span></div>
        </div>
        {footer && <div style={{ marginTop: 14 }}>{footer}</div>}
      </div>

      <style>{`@media (max-width: 900px){ .scope-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

function ScopeRow({ on, disabled, onToggle, label, blurb, amount }: {
  on: boolean; disabled?: boolean; onToggle: () => void; label: string; blurb: string; amount: string;
}) {
  return (
    <button onClick={onToggle} disabled={disabled} className="panel row between"
      style={{ padding: "12px 14px", cursor: disabled ? "not-allowed" : "pointer", textAlign: "left", opacity: disabled ? 0.5 : 1,
        borderColor: on ? "var(--accent)" : "var(--line)", background: on ? "var(--accent-soft)" : "var(--card)", gap: 12 }}>
      <span className="row" style={{ gap: 12 }}>
        <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: "grid", placeItems: "center",
          background: on ? "var(--accent)" : "transparent", border: "1.5px solid " + (on ? "var(--accent)" : "var(--line-2)"), color: "#fff" }}>
          {on && <Icon.check width={13} />}
        </span>
        <span><span style={{ display: "block", fontWeight: 500, fontSize: 14 }}>{label}</span><span className="dim" style={{ fontSize: 12 }}>{blurb}</span></span>
      </span>
      <span className="mono" style={{ fontSize: 13, color: on ? "var(--accent-ink)" : "var(--ink-3)", whiteSpace: "nowrap" }}>{amount}</span>
    </button>
  );
}
